import type { LocaleLanguage } from '@/lib/locale-context';
import { mapCardProps, MapCardType } from '@/utils/map-card-props';
import { CardProps } from '@/components/card/types';
import { mapCardContentHub } from '@/utils/map-card-content-hub';
import {
  contentTypeMap,
  ContentTypes,
  getContentTypePlural,
  getContentTypeSingular,
} from '@/utils/get-content-type';
import { buildStrapiUrl } from '../utils/strapi-url';
import { assertUpstreamOk, upstreamNotFound } from './assert-upstream';
import { fetchWithRetry } from './fetch-with-retry';
import type { PreviewStatus } from '@/lib/preview-status';

const CONTENT_SECTION_COMPONENTS = [
  'elements.rich-text',
  'elements.two-images',
  'elements.intro-header',
  'elements.feature-article',
  'elements.tip-box',
  'elements.image-content',
  'elements.content-cards',
  'elements.video-player',
  'elements.table',
  'sections.promo-block',
  'sections.swiper',
  'sections.faq',
];

const CONTENT_HUB_REVALIDATE_SECONDS = 1800;

export type ContentHubPagination = {
  start: number;
  limit: number;
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
  hasMore?: boolean;
};

export type ContentHubItemsPage = {
  items: CardProps[];
  pagination?: ContentHubPagination;
};

const getContentHubPayload = (payload: any) => {
  // Normalize the old aggregate-array response and the Strapi-like
  // `{ data, meta.pagination }` response into one local shape.
  if (Array.isArray(payload)) {
    return {
      data: payload,
      pagination: undefined,
    };
  }

  return {
    data: Array.isArray(payload?.data) ? payload.data : [],
    pagination: payload?.meta?.pagination as ContentHubPagination | undefined,
  };
};

/**
 * Fetches one article-like detail page by type and slug through the web facade.
 *
 * The response is split into content sections and remaining sections so detail
 * pages can render the article body separately from surrounding page sections.
 */
export async function fetchContentTypesBySlug(
  type: ContentTypes,
  slug: string,
  locale: LocaleLanguage,
  options: { status?: PreviewStatus } = {},
) {
  const { plural } = contentTypeMap[type];

  const params = new URLSearchParams({
    locale,
  });

  if (options.status) {
    params.set('status', options.status);
  }

  const fetchOptions: RequestInit = {};
  if (options.status) {
    fetchOptions.cache = 'no-store';
  }

  const res = await fetchWithRetry(
    buildStrapiUrl(
      `/api/web/content/${encodeURIComponent(plural)}/${encodeURIComponent(slug)}`,
      params,
    ),
    fetchOptions,
  );

  // Only an explicit upstream 404 or an empty result set means "not found";
  // anything else must throw so ISR keeps serving the stale page instead of
  // caching a 404 for the revalidate window.
  if (upstreamNotFound(res)) {
    return null;
  }
  assertUpstreamOk(res, `Fetch ${plural} "${slug}" (${locale})`);

  const pages = await res.json();
  if (!pages || !pages.data || pages.data.length === 0) {
    return null;
  }

  const sections = pages.data[0].contentZone || [];

  const contentSections = sections.filter((section: any) =>
    CONTENT_SECTION_COMPONENTS.includes(section.__component),
  );

  const remainingSections = sections.filter(
    (section: any) => !CONTENT_SECTION_COMPONENTS.includes(section.__component),
  );

  return {
    pageData: pages.data.pop(),
    remainingSections,
    contentSections,
  };
}

/**
 * Fetches one paginated mixed-type content hub page from the aggregate endpoint.
 *
 * The CMS merges the requested content families, sorts them globally by
 * published date, and returns a Strapi-like pagination object used by load more.
 */
export async function fetchContentHubItemsPage(
  locale: string,
  types: string[] = ['artikel', 'new', 'flash-talk', 'podcast', 'edu-talk'],
  categories: string[] = [],
  page: number = 1,
  limit: number = 6,
  linkTitle: string = 'Mehr lesen',
  artikelTranslation: string = 'Artikel',
): Promise<ContentHubItemsPage> {
  const start = (page - 1) * limit; // Correctly calculate the start index
  const normalizedTypes = types.map(getContentTypeSingular);

  // `/api/web/content-hub` is the aggregate facade endpoint. It accepts
  // singular type keys and can merge/sort several content families into one
  // paginated feed.
  const queryParams = new URLSearchParams({
    locale,
    types: normalizedTypes.join(','),
    categories: categories.join(','),
    'pagination[start]': start.toString(),
    'pagination[limit]': limit.toString(),
  });

  const response = await fetchWithRetry(
    buildStrapiUrl('/api/web/content-hub', queryParams),
    {
      next: {
        revalidate: CONTENT_HUB_REVALIDATE_SECONDS,
        tags: [`content-hub:${locale}`],
      },
    },
  );

  // This feed is the primary content of the hub page — a transient failure
  // must throw (stale page keeps serving) rather than cache an empty hub.
  assertUpstreamOk(response, `Fetch content hub (${locale})`);

  const payload = getContentHubPayload(await response.json());

  return {
    items: payload.data.map((item: any) =>
      mapCardContentHub(item, linkTitle, artikelTranslation),
    ),
    pagination: payload.pagination,
  };
}

/**
 * Fetches cards for related-content sections.
 *
 * For one concrete content type it calls `/api/web/collections/:type`; for
 * `all` it falls back to `/api/web/content-hub`. When a slug is provided, the
 * current item is excluded with `filters[slug][$ne]`.
 *
 * Unlike the detail fetchers, this deliberately degrades to `[]` on failure:
 * related-content strips are auxiliary, and failing the whole page render
 * over them is a worse trade than briefly caching the page without a strip.
 */
export async function fetchRelatedCollection(
  slug: string | null,
  contentType: ContentTypes | 'all',
  locale: LocaleLanguage,
  categoryTitles?: string[],
  limit = 3,
): Promise<CardProps[]> {
  let typeConfig: (typeof contentTypeMap)[ContentTypes] | null;
  if (contentType && contentType !== 'all') {
    typeConfig = contentTypeMap[contentType];
  } else {
    typeConfig = null;
  }

  // Prefer the facade collection endpoint for one concrete type. Fall back to
  // the aggregate content-hub facade endpoint only when related content can be "all".
  const baseUrl = buildStrapiUrl(
    typeConfig?.plural
      ? `/api/web/collections/${encodeURIComponent(typeConfig.plural)}`
      : '/api/web/content-hub',
  );

  const buildParams = (categories?: string[]): URLSearchParams => {
    const params = new URLSearchParams({
      locale,
      'pagination[limit]': String(limit),
    });

    // /api/web/content-hub needs singular types for aggregation.
    // /api/web/collections already receives the collection type in the route path.
    if (!typeConfig?.plural && typeConfig?.singular) {
      params.set('types', typeConfig.singular);
    }

    // Exclude same-slug item
    if (slug) {
      params.set('filters[slug][$ne]', slug);
    }

    // Categories filter optional
    if (categories?.length) {
      params.set('categories', categories.join(','));
    }

    return params;
  };

  const fetchContentHub = async (params: URLSearchParams) => {
    const tags = typeConfig?.plural
      ? [`collection:${locale}`, `collection:${locale}:${typeConfig.plural}`]
      : [`content-hub:${locale}`];

    const res = await fetchWithRetry(`${baseUrl}?${params.toString()}`, {
      next: {
        revalidate: CONTENT_HUB_REVALIDATE_SECONDS,
        tags,
      },
    });

    if (!res.ok) {
      console.error(`Failed to fetch content-hub items (${res.status})`);
      return [];
    }
    const payload = getContentHubPayload(await res.json());
    return payload.data;
  };

  try {
    // Try with categories first
    let hubData = await fetchContentHub(buildParams(categoryTitles));

    // Fallback: retry without categories if empty
    if (hubData.length === 0 && categoryTitles?.length) {
      hubData = await fetchContentHub(buildParams());
    }

    return hubData.flatMap((item: MapCardType) => {
      // Find plural type from singular
      const pluralType = (typeConfig?.plural ??
        (Object.values(contentTypeMap).find((ct) => ct.singular === item.type)
          ?.plural as ContentTypes | undefined)) as ContentTypes | undefined;

      if (!pluralType) {
        return [];
      }

      return [
        mapCardProps({
          ...item,
          type: pluralType,
          locale,
        }),
      ];
    });
  } catch (error) {
    console.error('Error fetching related content:', error);
    return [];
  }
}

/**
 * Fetches cards for one CMS `CollectionCards` section.
 *
 * The section provides one collection type, locale, optional category filters,
 * and an offset for load-more pagination. The helper calls the single-collection
 * `/api/web/collections/:type` facade and returns mapped cards plus `hasMore`.
 *
 * Deliberately degrades to `{ items: [], hasMore: false }` on failure: a
 * CollectionCards section is auxiliary page content, so a transient CMS error
 * should not fail the whole page render.
 */
export async function fetchItemsByCollectionPage(
  collectionType: string,
  locale: string = 'en',
  limit: number | null = 3,
  categories: string[] = [],
  offset: number = 0,
): Promise<{ items: CardProps[]; hasMore: boolean }> {
  limit = limit ?? 3;
  try {
    const normalizedCollectionType = getContentTypePlural(collectionType);

    // CollectionCards uses the web facade, so Strapi-specific populate details
    // stay inside cms-website.
    const params = new URLSearchParams({
      locale,
      'pagination[limit]': String(limit),
      'pagination[start]': String(offset),
    });

    if (categories.length) {
      params.set('categories', categories.join(','));
    }

    const res = await fetchWithRetry(
      buildStrapiUrl(
        `/api/web/collections/${encodeURIComponent(normalizedCollectionType)}`,
        params,
      ),
      {
        next: {
          revalidate: CONTENT_HUB_REVALIDATE_SECONDS,
          tags: [
            `collection:${locale}`,
            `collection:${locale}:${normalizedCollectionType}`,
          ],
        },
      },
    );

    if (!res.ok) {
      throw new Error(
        `Failed to fetch items from collection: ${collectionType}`,
      );
    }

    const data = await res.json();
    if (!data?.data.length) {
      return { items: [], hasMore: false };
    }

    const pagination = data.meta?.pagination as
      | ContentHubPagination
      | undefined;
    const sortedData = data.data.sort(
      (
        a: { publishedDate: string | number | Date },
        b: { publishedDate: string | number | Date },
      ) =>
        new Date(b.publishedDate).getTime() -
        new Date(a.publishedDate).getTime(),
    );

    return {
      items: sortedData.map((item: any) => mapCardProps(item)),
      hasMore: pagination
        ? pagination.start + sortedData.length < pagination.total
        : sortedData.length === limit,
    };
  } catch (error) {
    console.error('Error fetching items by collection:', error);
    return { items: [], hasMore: false };
  }
}
